import * as React from 'react';

import type {CueCardCtaAction} from 'shells/cue-card/cue-card';
import {CueCard} from 'shells/cue-card/cue-card';
import {GridList} from 'shells/grid-list/grid-list';
import {AgentsIcon} from 'shells/icon/svg/agents-icon';
import {FilterListRegularIcon} from 'shells/icon/svg/filter-list-regular-icon';
import {StarRegularIcon} from 'shells/icon/svg/star-regular-icon';
import {InternalSurveyModal} from 'shells/modal/internal-survey-modal/internal-survey-modal';
import {Body} from 'shells/typography/body';

import {launchIntercomMessenger} from '../../../intercom/launch-intercom-messenger';
import {useFeedbackSubmit} from '../../../internal-feedback/graphql/hooks/use-feedback-submit';

import {appHistory} from '../../../../history';

type HelpCard = {
    id: string;
    title: string;
    description: string;
    ctaText: string;
    actionType: CueCardCtaAction;
    url?: string;
    icon: React.ComponentType<any>;
    onClick?: () => void;
};

export function HelpSection(): React.ReactElement {
    const [isSurveyOpen, setIsSurveyOpen] = React.useState<boolean>(false);
    const {feedbackSubmit} = useFeedbackSubmit();

    const HELP_CARDS: HelpCard[] = [
        {
            id: 'contact',
            title: 'Contact us',
            description: 'Get help from our support team',
            actionType: 'modal',
            ctaText: 'Contact support team',
            icon: AgentsIcon,
            onClick: launchIntercomMessenger,
        },
        {
            id: 'feedback',
            title: 'Share your feedback',
            description: 'Tell us how we can improve Nutshell reports',
            actionType: 'modal',
            ctaText: 'Submit feedback',
            icon: StarRegularIcon,
            onClick: () => setIsSurveyOpen(true),
        },
        {
            id: 'saved-lists',
            title: 'Saved lists',
            description: 'Use saved companies, people, and leads for precise filtering',
            actionType: 'routed-link',
            ctaText: 'Go to leads list',
            url: '/leads',
            icon: FilterListRegularIcon,
        },
    ];

    return (
        <div className='flex flex-dir-col gap-16'>
            <Body isBold={true} color='dark' size='large' useDoyle={true}>
                Don’t see what you are looking for?
            </Body>
            <GridList numColumns={3}>
                {HELP_CARDS.map((card) => (
                    <CueCard
                        key={card.id}
                        title={card.title}
                        description={card.description}
                        icon={card.icon}
                        history={appHistory}
                        variant='feature'
                        cta={{
                            text: card.ctaText,
                            actionType: card.actionType,
                            url: card.url,
                            newTab: card.actionType === 'external-link',
                            onClick: card.onClick,
                        }}
                    />
                ))}
            </GridList>
            {isSurveyOpen && (
                <InternalSurveyModal
                    title='Tell us what you think about Nutshell reports'
                    description='Did you find the reports you are looking for? Is there anything we should improve about this feature?'
                    thumbnailIllustration='/include/images/report/report.svg'
                    onSubmit={(response: string) => {
                        return feedbackSubmit({
                            key: 'reports',
                            response,
                            uri: window.location.href,
                        });
                    }}
                    onClose={() => setIsSurveyOpen(false)}
                />
            )}
        </div>
    );
}
